import React, { useEffect, useRef } from "react";
import { Divider, Form, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../../features/save/saveSlice";
import { fetchData } from "../../../../features/fetch/fetchSlice";
import Inputs from "../sections/Inputs";
import AggregationMarkets from "../sections/AggregationMarkets";
import Infrastructure from "../sections/Infrastructure";
import ServicesGovernance from "../sections/ServicesGovernance";
import Challenges from "../sections/Challenges";
import Recommendations from "../sections/Recommendations";

function IndicatorsStep({ handleBack }) {
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const urlMarketChannel = `${process.env.REACT_APP_API_BASE_ROBOT}/market_channel/fetch_marketchannels`;
  const urlPest = `${process.env.REACT_APP_API_BASE_ROBOT}/pest_disease/fetch_pestdiseases`;
  const urlInfraStatus = `${process.env.REACT_APP_API_BASE_ROBOT}/infra_status/fetch_infrastatuss`;
  const urlMechType = `${process.env.REACT_APP_API_BASE_ROBOT}/mechanization/fetch_mechanizations`;
  const urlValueAddition = `${process.env.REACT_APP_API_BASE_ROBOT}/value_addition/fetch_valueadditions`;
  const urlExtension = `${process.env.REACT_APP_API_BASE_ROBOT}/extension_service/fetch_extensionservices`;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { saving } = useSelector((state) => state.save);
  const {
    activeObjProductionData,
    dataMarketChannel,
    dataPest,
    dataInfraStatus,
    dataMechType,
    dataValueAddition,
    dataExtension,
  } = useSelector((state) => ({
    activeObjProductionData: state.obj.activeObj["schemeProductionData"],
    dataMarketChannel: state.fetch.data[urlMarketChannel] || [],
    dataPest: state.fetch.data[urlPest] || [],
    dataInfraStatus: state.fetch.data[urlInfraStatus] || [],
    dataMechType: state.fetch.data[urlMechType] || [],
    dataValueAddition: state.fetch.data[urlValueAddition] || [],
    dataExtension: state.fetch.data[urlExtension] || [],
  }));

  const plainOptions = ["Yes", "No"];

  function handleCancel() {
    navigate("/production-data");
  }

  // Turns the selected ids into the {id, name} pairs the API stores
  const toIdNames = (ids, list, idKey, nameKey) =>
    Array.isArray(ids)
      ? ids?.map((id) => ({
          id: id,
          name: list?.find((item) => item?.[idKey] === id)?.[nameKey] ?? null,
        }))
      : [];

  const onFinish = async (data) => {
    const {
      operationalStatusId,
      marketChannelIds,
      pestIds,
      extensionServiceIds,
      valueAdditionIds,
      mechanizationTypeIds,
      ...rest
    } = data;

    
    const status = dataInfraStatus?.find(
      (item) => item?.infsId === operationalStatusId
    );

    const payload = {
      ...rest,
      id: activeObjProductionData?.id ?? null, 
      submit: true,
      operationalStatus: operationalStatusId
        ? { id: operationalStatusId, name: status?.infsName ?? null }
        : null,
      marketChannels: toIdNames(
        marketChannelIds,
        dataMarketChannel,
        "mcId",
        "mcChannel"
      ),
      pestsControlled: toIdNames(pestIds, dataPest, "pdId", "pdPest"),
      extensionServices: toIdNames(
        extensionServiceIds,
        dataExtension,
        "esId",
        "esProvider"
      ),
      valueAdditionCapacity: toIdNames(
        valueAdditionIds,
        dataValueAddition,
        "vaId",
        "vaType"
      ),
      mechanizationTypes: toIdNames(
        mechanizationTypeIds,
        dataMechType,
        "mechId",
        "mechName"
      ),
    };

    const res = await dispatch(
      save({
        ...payload,
        url: "api/v1/scheme-production-data/save",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  useEffect(() => {
    dispatch(fetchData({ url: urlMarketChannel, limit: 1000, start: 0 }));
    dispatch(fetchData({ url: urlPest, limit: 1000, start: 0 }));
    dispatch(fetchData({ url: urlInfraStatus, limit: 1000, start: 0 }));
    dispatch(fetchData({ url: urlMechType, limit: 1000, start: 0 }));
    dispatch(fetchData({ url: urlValueAddition, limit: 1000, start: 0 }));
    dispatch(fetchData({ url: urlExtension, limit: 1000, start: 0 }));
  }, []);

  useEffect(() => {
    if (!activeObjProductionData?.id) {
      return;
    }
    form.setFieldsValue({
      ...activeObjProductionData,
      operationalStatusId: activeObjProductionData?.operationalStatus?.id ?? null,
      marketChannelIds: activeObjProductionData?.marketChannels?.map(
        (item) => item?.id
      ),
      pestIds: activeObjProductionData?.pestsControlled?.map(
        (item) => item?.id
      ),
      extensionServiceIds: activeObjProductionData?.extensionServices?.map(
        (item) => item?.id
      ),
      valueAdditionIds: activeObjProductionData?.valueAdditionCapacity?.map(
        (item) => item?.id
      ),
      mechanizationTypeIds: activeObjProductionData?.mechanizationTypes?.map(
        (item) => item?.id
      ),
    });
  }, [activeObjProductionData, form]);

  return (
    <>
      <Form
        layout="vertical"
        ref={formRef}
        name="control-ref"
        onFinish={onFinish}
        className=""
        style={{
          maxWidth: "100%",
        }}
        form={form}
      >
        <Inputs />

        <AggregationMarkets
          plainOptions={plainOptions}
          form={form}
          dataMarketChannel={dataMarketChannel}
        />

        <Infrastructure
          plainOptions={plainOptions}
          form={form}
          dataInfraStatus={dataInfraStatus}
        />

        <ServicesGovernance
          plainOptions={plainOptions}
          form={form}
          dataPest={dataPest}
          dataExtension={dataExtension}
          dataValueAddition={dataValueAddition}
        />

        <Challenges dataMechType={dataMechType} />

        <Recommendations />
      </Form>

      <div className="sticky bottom-0 left-0 w-full bg-white">
        <Divider />
        <div className="flex justify-between mt-[1rem] gap-x-[1.25rem]">
          <div className="w-[100px]">
            <button
              type="button"
              key="back"
              onClick={handleBack}
              className="cstm-btn"
            >
              Back
            </button>
          </div>

          <div className="w-[100px]">
            <button
              onClick={() => form.submit()}
              disabled={saving}
              type="button"
              className="cstm-btn-2"
            >
              {saving ? <Spin /> : "Finish"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default IndicatorsStep;
